<template>
  <vuetify-select
    v-bind="$attrs"
    v-on="$listeners"
    :value="localValue"
    @input="onInput"
    class="base-select"
    :class="dynamicClasses"
    append-icon="mdi-chevron-down"
    :persistent-placeholder="true"
    outlined
    :menu-props="menuProps"
    color="red"
  >
    <template #label>
      {{ label }}
    </template>

    <template
      v-for="(slotContent, slotName) in slots"
      v-slot:[slotName]="{ item }"
    >
      <slot :name="slotName" :item="item" />
    </template>

    <template #prepend-inner>
      <slot name="prepend-inner" />
    </template>
  </vuetify-select>
</template>

<script>
import { VSelect } from 'vuetify/lib/components'

export default {
  name: 'BaseSelect',
  components: {
    'vuetify-select': VSelect,
  },
  data() {
    return {
      localValue: this.value || '',
    }
  },
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: '',
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['medium', 'small'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    menuProps: {
      type: Object,
      default: () => ({
        'content-class': 'base-select',
      }),
    },
  },
  methods: {
    onInput(val) {
      this.localValue = val
      this.$emit('input', val)
    },
  },
  computed: {
    slots() {
      const resultSlots = { ...this.$slots, ...this.$scopedSlots }
      delete resultSlots.label // remove duplicata de slot
      return resultSlots
    },
    isFilled() {
      return Boolean(this.localValue && this.localValue.length)
    },
    dynamicClasses() {
      const returnObj = {
        filled: this.isFilled,
      }
      returnObj[`size--${this.size}`] = true
      return returnObj
    },
  },
  watch: {
    value(val) {
      this.localValue = val
    },
    localValue(val) {
      this.$emit('input', val)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-list-item--active {
  background-color: varFigma(brand-95-lighter) !important;
  color: varFigma(brand-40-dark) !important;
}
.base-select.v-input {
  &::v-deep {
    .v-select__slot .v-select__selection {
      font-weight: 400;
      font-size: 14px;
      &::placeholder {
        font-weight: 400;
        font-size: 14px;
        color: varFigma(neutral-60) !important;
      }
      @media only screen and (max-width: 960px) {
        font-size: 16px;
        &::placeholder {
          font-size: 16px;
        }
      }
    }

    .v-select__slot {
      .v-label {
        top: -20px;
        left: -12px !important; // default text-field padding
        font-size: 12px;
        font-weight: 500;
        color: varFigma(neutral-40-light);
        &--active {
          transform: none !important;
          color: varFigma(neutral-20-dark) !important;
        }
      }
    }

    .v-input__append-inner i {
      color: varFigma(neutral-40-light) !important;
    }

    fieldset {
      padding-left: 0 !important;
      height: 40px;
      top: 0px;
      border: 1px solid varFigma(neutral-95-lightest);

      legend {
        display: none !important;
      }
    }

    &.filled {
      .v-select__slot {
        .v-label {
          color: varFigma(neutral-20-dark) !important;
        }
      }

      .v-select__slot .v-select__selection {
        color: varFigma(neutral-40-light) !important;
      }

      .v-input__append-inner i {
        color: varFigma(neutral-40-light) !important;
      }

      &.v-input--is-focused {
        .v-select__slot .v-select__selection {
          color: varFigma(neutral-30-base) !important;
        }

        .v-input__append-inner i {
          color: varFigma(neutral-30-base) !important;
        }
      }

      &:hover {
        .v-select__slot .v-select__selection {
          color: varFigma(neutral-30-base) !important;
        }
        .v-input__append-inner i {
          color: varFigma(neutral-30-base) !important;
        }
      }
    }

    &.v-input--is-focused {
      fieldset {
        border: 2px solid varFigma(brand-60-base) !important;
      }
    }

    &:hover {
      .v-label {
        color: varFigma(neutral-20-dark);
      }

      fieldset {
        border: 1px solid varFigma(brand-80-light);
      }
    }

    &.error--text {
      .v-select__slot {
        .v-label {
          color: varFigma(feedback-danger) !important;
        }
      }

      fieldset {
        border: 1px solid varFigma(feedback-danger-light) !important;
        background-color: varFigma(feedback-danger-background) !important;
      }

      .v-select__slot .v-select__selection {
        color: varFigma(feedback-danger-dark) !important;
      }

      .v-input__append-inner i {
        color: varFigma(feedback-danger-dark) !important;
      }
    }

    &.v-input--is-disabled {
      .v-select__slot {
        .v-label {
          color: varFigma(neutral-70) !important;
        }
      }

      .v-select__slot .v-select__selection {
        color: varFigma(neutral-70) !important;
      }

      .v-input__append-inner i {
        color: varFigma(neutral-70) !important;
      }

      fieldset {
        border: 1px solid varFigma(neutral-80-lighter) !important;
        background-color: varFigma(neutral-95-lightest) !important;
      }
    }

    &.v-input--is-readonly {
      .v-input__slot {
        padding: 0 !important;
      }
      fieldset {
        display: none !important;
      }
      .v-select__slot {
        .v-label {
          color: varFigma(neutral-40-light) !important;
          left: 0px !important;
          top: -8px !important;
        }
      }
      .v-select__slot .v-select__selection {
        color: varFigma(neutral-60) !important;
        &::placeholder {
          color: varFigma(neutral-60) !important;
        }
      }
      .v-input__append-inner i {
        color: varFigma(neutral-60) !important;
      }
      &.filled {
        .v-select__slot {
          .v-label {
            color: varFigma(neutral-20-dark) !important;
          }
        }
        .v-select__slot .v-select__selection {
          color: varFigma(neutral-40-light) !important;
        }

        .v-input__append-inner i {
          // color: varFigma(neutral-40-light) !important;
          color: transparent !important;
        }
      }
    }

    &.size {
      &--small {
        .v-select__slot {
          font-size: 12px;
          &::placeholder {
            font-size: 12px;
          }
        }

        .v-input__slot {
          min-height: 32px !important;
        }

        fieldset,
        .v-select__slot {
          height: 32px;
        }
      }
    }
  }
}
</style>
